import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { csrfCookie, getMe, ipecsLogin, setIpecsUserInfo, updateMe } from "../api/auth";
import SplashScreen from "../components/SplashScreen";
import { storageKeys } from "../constants";

function createDeferred() {
  const deferred = { settled: false };
  deferred.promise = new Promise((resolve) => {
    deferred.resolve = () => {
      deferred.settled = true;
      resolve();
    };
  });
  return deferred;
}

// Ceiling on how long a caller will wait for the iPECS SDK login to settle,
// in case auth never resolves (e.g. it needs a manual relogin).
const MAX_AUTH_WAIT_MS = 8000;

const AuthContext = React.createContext();
const AuthProvider = ({ children }) => {
  const [apiUser, setApiUser] = useState();
  const [ipecsAuthentciated, setIpecsAuthenticated] = useState(false);
  const [ipecsUser, setIpecsUser] = useState();
  const [ipecsAuthData, setIpecsAuthData] = useState();
  const [loggingOut, setLoggingOut] = useState(false);
  const [isPhoneControl, setIsPhoneControl] = useState();
  const [isAcd, setIsAcd] = useState(
    localStorage.getItem(storageKeys.ACD_ENABLED) ?? false,
  );

  // Lets a caller (e.g. click-to-dial) await the moment the iPECS SDK login
  // actually completes, instead of guessing at a fixed delay. Re-armed each
  // time authentication drops so the next login cycle gets its own gate.
  const ipecsReadyRef = useRef(null);
  if (!ipecsReadyRef.current) {
    ipecsReadyRef.current = createDeferred();
  }

  useEffect(() => {
    if (ipecsAuthentciated) {
      ipecsReadyRef.current.resolve();
    } else if (ipecsReadyRef.current.settled) {
      ipecsReadyRef.current = createDeferred();
    }
  }, [ipecsAuthentciated]);

  // A reissue/relogin can run while ipecsAuthentciated is still true (e.g.
  // triggered by an SDK access-token-expired callback), so that flag alone
  // doesn't cover the window where the session is being replaced. Callers
  // that are about to attempt one call this first to re-arm the gate.
  const markIpecsAuthPending = useCallback(() => {
    if (ipecsReadyRef.current.settled) {
      ipecsReadyRef.current = createDeferred();
    }
  }, []);

  const waitForIpecsReady = useCallback(() => {
    return Promise.race([
      ipecsReadyRef.current.promise,
      new Promise((resolve) => setTimeout(resolve, MAX_AUTH_WAIT_MS)),
    ]);
  }, []);

  useEffect(() => {
    csrfCookie();
  }, []);

  useEffect(() => {
    if (ipecsUser?.myInfo) {
      const body = {};

      const ext = (ipecsUser.myInfo.callNumbers ?? []).find(
        (item) => item.type === "extension",
      );

      if (ext) {
        body.ext = ext.value.includes("*") ? ext.value.split("*")[1] : ext.value;
      }

      if (ipecsUser.myInfo.wucUserId ?? false) {
        body.id = ipecsUser.myInfo.wucUserId;
      }

      if (Object.keys(body).length > 0) {
        ipecsLogin(body).catch(() => {});
      }

      updateMe({ phone_control: isPhoneControl }).catch(() => {});

      setIpecsUserInfo({
        myInfo: ipecsUser.myInfo,
        userSetting: ipecsUser.userSetting,
      }).catch(() => {});
    }
  }, [ipecsUser?.myInfo, ipecsUser?.userSetting, isPhoneControl]);

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    retry: false,
    onError: () => setApiUser(),
  });

  useEffect(() => {
    if (meQuery?.data) {
      setApiUser(meQuery.data.data.data);
      setIsPhoneControl(meQuery.data.data.data.phone_control ?? false);
    }
  }, [meQuery.data]);

  useEffect(() => {
    if (
      apiUser &&
      apiUser.hasOwnProperty("notifications") &&
      Array.isArray(apiUser.notifications)
    ) {
      apiUser.notifications.map((x) => {
        return toast(
          <div dangerouslySetInnerHTML={{ __html: x.message }}></div>,
          {
            type: x.type,
            autoClose: x.auto_close,
          },
        );
      });
    }
  }, [apiUser]);

  const hasFeature = useCallback(
    (features) => {
      return (
        apiUser &&
        apiUser.features &&
        apiUser.features.length > 0 &&
        apiUser.features.some((r) => features.includes(r))
      );
    },
    [apiUser],
  );

  const hasIntegration = (integrations) => {
    return (
      apiUser &&
      apiUser.integrations &&
      apiUser.integrations.length > 0 &&
      apiUser.integrations.some((r) => integrations.includes(r))
    );
  };

  const defaultContext = {
    apiUser,
    setApiUser,
    ipecsUser,
    ipecsAuthData,
    isPhoneControl,
    isAcd,
    ipecsAuthentciated,
    waitForIpecsReady,
    markIpecsAuthPending,
    setIpecsAuthenticated,
    setIpecsUser,
    setIpecsAuthData,
    setLoggingOut,
    setIsPhoneControl,
    setIsAcd,
    hasFeature,
    hasIntegration,
  };
  return (
    <AuthContext.Provider value={defaultContext}>
      {meQuery.isLoading || loggingOut ? <SplashScreen /> : <>{children}</>}
    </AuthContext.Provider>
  );
};

function useAuth() {
  return useContext(AuthContext);
}

export { AuthProvider, useAuth };
